import { api } from './client'

export interface CareLogFile {
    id: string
    url: string
    name: string
    mime_type?: string
}

export type CareLogActivityType =
    | 'fertilizing'
    | 'harvesting'
    | 'replanting'
    | 'spraying'
    | 'weeding'
    | 'watering'
    | 'pruning'
    | 'bud_picking'
    | 'other'

/** A household area (vùng trồng) for the parcel picker. `id` is what a care log
 *  references; code/name are for display. */
export interface CareLogArea {
    id: string
    code: string
    name: string
    acreage: number
    /** Toạ độ thửa đã vẽ trên bản đồ; vùng chưa vẽ thì thiếu cả hai. */
    paths?: unknown[]
    markers?: unknown[]
}

/** One HTX material used in a care log + định lượng riêng của vật tư đó. */
export interface CareLogMaterial {
    agricultural_material_id: string
    /** Định lượng đã dùng, theo đơn vị của vật tư. Nguồn số liệu để xuất kho. */
    quantity: number
    /** Hộ tự mua vật tư này ở ngoài (không lấy kho HTX) → dòng này không xuất
     *  kho. Vật tư ngoài (HTX không theo dõi tồn) luôn gửi true. */
    self_bought?: boolean
    /** Read-only: hydrated by GET so the edit form can label the material.
     *  Absent on the write path. */
    material_name?: string
    material_image_url?: string | null
    /** Read-only: đơn vị của vật tư (kg|bag|bottle|pack) để hiện cạnh ô định lượng. */
    material_unit?: string
}

export interface CareLog {
    id: string
    grower_id: string
    /** Hydrated by the list endpoint from a join on growers, and it survives the
     *  household being soft-deleted — that is how a row keeps its label after the
     *  household leaves every picker. Detail endpoints leave it undefined. */
    grower_name?: string
    activity_type: CareLogActivityType
    log_date: string
    parcels: string[]
    quantity: number
    quarantine_note?: string
    note?: string
    weighing_id?: string
    materials: CareLogMaterial[]
    /** Vùng trồng đã chọn, đã đối chiếu `parcels` với hồ sơ hộ — chỉ endpoint
     *  danh sách trả về (card cần tên/diện tích/toạ độ để vẽ chip và bản đồ). */
    areas?: CareLogArea[]
    implementer_ids: string[]
    /** Tên người thực hiện, chỉ endpoint danh sách trả về: tài khoản hộ dân
     *  không có quyền đọc danh sách người dùng để tự tra id → tên. */
    implementer_names?: string[]
    files?: CareLogFile[]
    created_at: string
    updated_at: string
}

export interface UpsertCareLogRequest {
    grower_id: string
    activity_type: CareLogActivityType
    log_date: string
    parcels: string[]
    quantity: number
    quarantine_note?: string | null
    note?: string | null
    weighing_id?: string | null
    materials: CareLogMaterial[]
    file_ids: string[]
    implementer_ids: string[]
}

/** A household linked to the current account, for the grower-scoped picker. */
export interface MyGrower {
    id: string
    name: string
}

/** Hộ + vùng mà mã QR vùng trồng trỏ tới — chỉ trả khi tài khoản được ghi nhật
 *  ký cho hộ đó (ngoài phạm vi server trả 404). */
export interface CareLogAreaContext {
    area_id: string
    grower_id: string
    grower_name: string
}

export interface ListCareLogsParams {
    grower_id?: string
    /** Chỉ lấy nhật ký có ghi vùng trồng này trong `parcels`. */
    area_id?: string
    activity_type?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export const careLogsApi = {
    list: (params?: ListCareLogsParams) =>
        api.get<CareLog[]>('/care-logs', {
            params: params as Record<string, string | number | undefined>,
        }),
    /** Excel nhật ký theo đúng bộ lọc danh sách (không phân trang). Trả nguyên
     *  response để đọc tên file trong Content-Disposition. */
    exportExcel: (params?: ListCareLogsParams) =>
        api.getBlob('/care-logs/export', { params }),
    getById: (id: string) => api.get<CareLog>(`/care-logs/${id}`),
    /** Households linked to the current account (grower-scoped picker). */
    myGrowers: () => api.get<MyGrower[]>('/care-logs/my-growers'),
    /** A household's areas (vùng trồng) — the source for the parcel picker. */
    growerAreas: (growerId: string) =>
        api.get<CareLogArea[]>(`/care-logs/growers/${growerId}/areas`),
    /** Hộ + vùng của một mã QR vùng trồng, nếu tài khoản được ghi nhật ký cho hộ đó. */
    areaContext: (areaId: string) =>
        api.get<CareLogAreaContext>(`/care-logs/areas/${areaId}`),
    create: (data: UpsertCareLogRequest) =>
        api.post<CareLog>('/care-logs', data),
    update: (id: string, data: UpsertCareLogRequest) =>
        api.put<CareLog>(`/care-logs/${id}`, data),
    delete: (id: string) => api.delete(`/care-logs/${id}`),
}
